// Terms & Conditions and Privacy Policy text for PaceSlot, a product of
// Kalgyan Solutions. Kept as data so the web pages (Terms.jsx, Privacy.jsx)
// and the mobile app's copy (KBA-Mobile src/content/legal.ts) stay word-for-
// word identical — edit both together.
//
// Each section: { title, paragraphs?: string[], bullets?: string[], after?: string[] }

export const COMPANY = {
  name: 'Kalgyan Solutions',
  product: 'PaceSlot',
  address: 'IB Circle, Ramanathapura, Karnataka, India',
  email: 'support@kalgyan.in',
};

export const LEGAL_LAST_UPDATED = 'October 2026';

export const TERMS_INTRO = [
  'PaceSlot ("PaceSlot" or the "Platform") is a product owned and operated by Kalgyan Solutions ("Kalgyan Solutions", "we", "us" or "our"). These Terms & Conditions govern your use of the PaceSlot website, mobile applications and related services.',
  'By creating an account, making a booking or otherwise using the Platform, you agree to these Terms. If you do not agree, please do not use the Platform.',
];

export const TERMS_SECTIONS = [
  {
    title: 'About PaceSlot',
    paragraphs: [
      'PaceSlot is a technology platform that helps players discover sports venues and academies ("Venues"), book courts, buy memberships and manage their bookings, and helps Venues manage their courts, schedules, memberships and payments.',
      'Venues are independent businesses that list on PaceSlot. The Venue, not Kalgyan Solutions, provides the courts, coaching, equipment and facilities, and is responsible for their quality and safety and for conduct on its premises.',
    ],
  },
  {
    title: 'Eligibility',
    paragraphs: [
      'You must be at least 18 years old, or use the Platform under the supervision of a parent or guardian who accepts these Terms on your behalf.',
      'You confirm that the information you provide is accurate and complete, and that you will keep it up to date.',
    ],
  },
  {
    title: 'Your Account',
    bullets: [
      'You are responsible for keeping your login credentials secure and must not share them.',
      'You are responsible for all activity under your account.',
      'Tell us promptly at the contact details below if you believe your account has been misused.',
    ],
  },
  {
    title: 'Bookings & Payments',
    paragraphs: [
      'Prices for courts, memberships and other services are set by each Venue and shown before you pay. A booking is confirmed only after your payment succeeds; a slot that is not paid for within the checkout window is released.',
      'Payments are processed securely by Razorpay, our payment gateway partner. Depending on the Venue, your payment is collected either directly by the Venue through its own Razorpay account or by Kalgyan Solutions on the Venue\'s behalf. We do not store your card, UPI or bank details.',
    ],
    bullets: [
      'Bookings are subject to availability and to the Venue\'s house rules.',
      'A Venue may cancel or reschedule a booking where necessary; if it cancels, you receive a full refund.',
      'Membership plans are personal, non-transferable and valid for the plan period from activation.',
    ],
  },
  {
    title: 'Cancellations & Refunds',
    paragraphs: [
      'Cancellations and refunds are governed by our Refund & Cancellation Policy, which forms part of these Terms. In short: the refund for a booking you cancel depends on how much notice you give, venue cancellations are refunded in full, and memberships are non-refundable once activated except where the Venue cannot provide the service.',
    ],
  },
  {
    title: 'Terms for Venues',
    paragraphs: [
      'If you list a Venue on PaceSlot or manage one as an administrator, you also agree that:',
    ],
    bullets: [
      'Your listing, prices, availability and business details are accurate, and you will honour confirmed bookings.',
      'You will complete Razorpay\'s onboarding and KYC to receive online payments, and keep your payout account in good standing.',
      'Kalgyan Solutions charges a platform fee per online booking and per membership as agreed with you. Fees are invoiced monthly and collected through your UPI AutoPay / e-mandate or a payment link.',
      'If an invoice remains unpaid past its due date, online bookings for your Venue may be paused until it is settled.',
      'You are responsible for your own taxes, licences and compliance with applicable law.',
    ],
  },
  {
    title: 'Acceptable Use',
    paragraphs: ['You agree not to:'],
    bullets: [
      'Provide false or misleading information, or impersonate anyone',
      'Use the Platform for any unlawful or fraudulent purpose',
      'Attempt unauthorised access to the Platform, its systems or other users\' accounts',
      'Upload harmful, abusive or offensive content, including in reviews',
      'Interfere with or disrupt the operation of the Platform',
    ],
  },
  {
    title: 'Limitation of Liability',
    paragraphs: [
      'Sports involve inherent risk, and you take part voluntarily and at your own risk. Kalgyan Solutions provides the Platform as an intermediary between players and Venues and, to the extent permitted by law, is not liable for:',
    ],
    bullets: [
      'Injuries, accidents or illness during play, coaching or training',
      'The condition of a Venue\'s courts, equipment or facilities, or the acts of its staff',
      'Loss of or damage to personal belongings',
      'Temporary interruptions of the Platform or of third-party services such as payment gateways',
    ],
  },
  {
    title: 'Intellectual Property',
    paragraphs: [
      'The PaceSlot name, logo, design, text, graphics and software are the property of Kalgyan Solutions. Venue names, logos and photos belong to the respective Venues.',
      'You may not copy, distribute or commercially use any Platform content without our prior written permission.',
    ],
  },
  {
    title: 'Privacy',
    paragraphs: ['Your use of the Platform is also governed by our Privacy Policy.'],
  },
  {
    title: 'Suspension & Termination',
    paragraphs: [
      'We may suspend or terminate accounts or Venue listings that violate these Terms or misuse the Platform. You may stop using the Platform and ask us to delete your account at any time.',
    ],
  },
  {
    title: 'Changes to These Terms',
    paragraphs: [
      'We may update these Terms from time to time. The "Last updated" date below shows when they last changed. Continuing to use the Platform after an update means you accept the revised Terms.',
    ],
  },
  {
    title: 'Governing Law',
    paragraphs: [
      'These Terms are governed by the laws of India. Any dispute is subject to the jurisdiction of the courts in Karnataka, India.',
    ],
  },
];

export const PRIVACY_INTRO = [
  'Kalgyan Solutions ("Kalgyan Solutions", "we", "us" or "our") owns and operates PaceSlot. We are committed to respecting and protecting your privacy.',
  'This Privacy Policy explains what information we collect when you use the PaceSlot website and mobile applications (the "Platform"), how we use and share it, and the choices you have. By using the Platform, you agree to this Policy.',
];

export const PRIVACY_SECTIONS = [
  {
    title: 'Information We Collect',
    bullets: [
      'Account details: your name, email address, mobile number and, if you sign in with Google, your Google profile name and picture',
      'Booking and membership details: venues, courts, slots, plans and your booking history',
      'Payment details: transaction IDs, amounts and payment status. Card, UPI and bank details are handled by Razorpay and are not stored by us',
      'Location: your approximate location, only if you allow it, to show venues near you',
      'Device information: device type, app version, notification token and diagnostics that keep the app working',
      'Messages you send us, and reviews you post',
    ],
    after: [
      'If you manage a Venue, we also collect business and billing contact details and your Razorpay account ID. KYC and bank details for payouts are submitted directly to Razorpay.',
    ],
  },
  {
    title: 'How We Use Your Information',
    bullets: [
      'Create and manage your account',
      'Process bookings, memberships, payments and refunds',
      'Send confirmations, reminders and service updates by email, SMS, WhatsApp or push notification',
      'Show venues near you and improve the Platform',
      'Provide customer support',
      'Bill Venues for platform fees',
      'Prevent fraud and misuse, and comply with legal obligations',
    ],
  },
  {
    title: 'Sharing of Information',
    paragraphs: [
      'We do not sell or rent your personal information. We share it only as needed to run the Platform:',
    ],
    bullets: [
      'With the Venue you book or buy a membership from: your name, contact details and booking details, so it can serve you',
      'With Razorpay, to process payments and refunds',
      'With hosting, email, messaging, notification and maps providers who work on our behalf',
      'With government or law-enforcement authorities where required by law',
    ],
  },
  {
    title: 'Data Retention',
    paragraphs: [
      'We keep your information for as long as your account is active and afterwards only as long as needed for legal, tax and accounting purposes or to resolve disputes.',
    ],
  },
  {
    title: 'Security',
    paragraphs: [
      'We use reasonable technical and organisational safeguards to protect your information from unauthorised access, misuse or disclosure. No online service can guarantee complete security.',
    ],
  },
  {
    title: 'Your Choices & Rights',
    bullets: [
      'View and update your profile in the app',
      'Ask us for a copy of your data, or to correct or delete it',
      'Turn off location access or notifications in your device settings',
      'Revoke Google sign-in access from your Google account',
    ],
    after: ['To make a request, contact us using the details below.'],
  },
  {
    title: 'Children',
    paragraphs: [
      'The Platform is not meant for children under 18 without the supervision of a parent or guardian. If you believe a child has given us personal information without consent, please contact us and we will delete it.',
    ],
  },
  {
    title: 'Third-Party Services',
    paragraphs: [
      'The Platform uses or links to third-party services such as Razorpay, Google sign-in and Google Maps. Their privacy practices are governed by their own policies.',
    ],
  },
  {
    title: 'Changes to This Policy',
    paragraphs: [
      'We may update this Policy from time to time. The "Last updated" date below shows when it last changed. Continuing to use the Platform after an update means you accept the revised Policy.',
    ],
  },
];
